import { useDeferredValue, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { ArrowLeft, Eye, EyeOff, CheckCircle, Briefcase } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { authApi } from '@/services/auth'
import { BackendServerSelector } from '@/components/auth/backend-server-selector'
import { getErrorMessage } from '@/lib/api'
import { useSelectedBackendServerUrl } from '@/lib/backend-servers'
import { orgTreeApi } from '@/services/org-tree'
import { useAuthStore } from '@/stores/auth-store'
import { buildPostSelectionPath } from '@/lib/auth-redirect'
import { withServiceInstanceId } from '@/lib/service-instance'

const resetPasswordSchema = z
  .object({
    new_password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain uppercase, lowercase, and number'
      ),
    confirm_password: z.string(),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  })

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const autoLoginCredential = searchParams.get('credential')?.trim() || ''
  const autoLoginMethod = searchParams.get('login_method') === 'phn_nbr' ? 'phn_nbr' : 'email'
  const selectedBackendServerUrl = useSelectedBackendServerUrl()
  const queryTenant = searchParams.get('org')
    || searchParams.get('id')
    || searchParams.get('tenant')
    || searchParams.get('company_identifier')
    || searchParams.get('service_instance_id')
    || ''
  const deferredTenant = useDeferredValue(queryTenant.trim())

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const organizationQuery = useQuery({
    queryKey: ['reset-password-service-instance', selectedBackendServerUrl, deferredTenant],
    queryFn: () => orgTreeApi.resolvePublicSignupServiceInstance(deferredTenant),
    enabled: deferredTenant.length >= 3,
    staleTime: 60_000,
    retry: false,
  })

  const organization = organizationQuery.data
  const organizationName = organization?.name
    || organization?.title
    || organization?.identifier
    || organization?.url_identifier
    || 'Ellan One'
  const organizationLogo = organization?.login_page_logo
    || organization?.logo
    || organization?.welcome_page_logo
    || null
  const loginTenant = organization?.identifier || organization?.instance_id || queryTenant
  const loginHref = loginTenant
    ? `/login?tenant=${encodeURIComponent(loginTenant)}`
    : '/login'
  const forgotPasswordHref = loginTenant
    ? `/forgot-password?tenant=${encodeURIComponent(loginTenant)}`
    : '/forgot-password'

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  })

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      toast.error('Invalid reset link. Please request a new one.')
      return
    }

    setIsLoading(true)
    try {
      const resetResponse = await authApi.resetPassword({
        token,
        new_password: data.new_password,
        confirm_password: data.confirm_password,
      })

      const resetData = (resetResponse?.data ?? {}) as Record<string, unknown>
      const responseTenant = String(resetData.service_instance_id ?? resetData.tenant ?? '').trim()
      const responseEmail = String(resetData.email ?? '').trim()
      const responsePhone = String(resetData.phn_nbr ?? '').trim()

      const resolvedAutoLoginTenant = String(loginTenant || responseTenant || '').trim()
      const resolvedAutoLoginMethod = autoLoginCredential
        ? autoLoginMethod
        : responseEmail
          ? 'email'
          : responsePhone
            ? 'phn_nbr'
            : autoLoginMethod
      const resolvedAutoLoginCredential = String(
        autoLoginCredential
        || (resolvedAutoLoginMethod === 'email' ? responseEmail : responsePhone)
        || '',
      ).trim()
      const canAutoLogin = Boolean(resolvedAutoLoginTenant && resolvedAutoLoginCredential)

      if (canAutoLogin) {
        try {
          const serviceInstanceReference = String(organization?.instance_id || organization?.id || resolvedAutoLoginTenant).trim()
          const loginResponse = await authApi.login({
            tenant: resolvedAutoLoginTenant,
            service_instance_id: serviceInstanceReference || undefined,
            password: data.new_password,
            type: resolvedAutoLoginMethod,
            ...(resolvedAutoLoginMethod === 'phn_nbr'
              ? { phn_nbr: resolvedAutoLoginCredential }
              : { email: resolvedAutoLoginCredential }),
          })

          if (loginResponse.data?.access_token) {
            const authState = useAuthStore.getState()
            const currentServiceInstanceId = authState.serviceInstanceId
            const postsCount = authState.availablePosts.length || (loginResponse.data?.posts?.length ?? 0)
            toast.success('Password reset successful. Signed in.')
            navigate(
              postsCount > 0
                ? buildPostSelectionPath(currentServiceInstanceId, null)
                : withServiceInstanceId('/profile', currentServiceInstanceId || null),
              { replace: true },
            )
            return
          }
        } catch {
          // Fallback to the success page if automatic login cannot be completed.
        }
      }

      setIsSuccess(true)
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }

  if (!token) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-background to-cyan-50/30 px-4 py-10">
        <div aria-hidden className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-cyan-200/30 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-orange-200/30 blur-3xl" />
        <div className="mx-auto w-full max-w-md">
          <Card className="border-border/70 bg-card/95 shadow-xl shadow-slate-900/5 backdrop-blur">
            <CardContent className="pt-6">
              <div className="space-y-4 text-center">
                <h2 className="text-xl font-semibold text-destructive">Invalid Reset Link</h2>
                <p className="text-sm text-muted-foreground">
                  This password reset link is invalid or has expired.
                </p>
                <Button asChild className="w-full">
                  <Link to={forgotPasswordHref}>Request a new link</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (isSuccess) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-background to-cyan-50/30 px-4 py-10">
        <div aria-hidden className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-cyan-200/30 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-orange-200/30 blur-3xl" />
        <div className="mx-auto w-full max-w-md">
          <div className="mb-8 flex flex-col items-center">
            {organizationLogo ? (
              <img src={organizationLogo} alt={`${organizationName} logo`} className="max-h-20 w-auto rounded-xl border bg-card p-2" />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success text-success-foreground">
                <CheckCircle className="h-6 w-6" />
              </div>
            )}
            <h1 className="mt-4 text-2xl font-bold text-foreground">Password reset</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Your password has been successfully reset
            </p>
          </div>

          <Card className="border-border/70 bg-card/95 shadow-xl shadow-slate-900/5 backdrop-blur">
            <CardContent className="pt-6">
              <BackendServerSelector disabled={isLoading} className="mb-4" />
              <Button className="w-full" onClick={() => navigate(loginHref)}>
                Continue to sign in
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-background to-cyan-50/30 px-4 py-10">
      <div aria-hidden className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-cyan-200/30 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-orange-200/30 blur-3xl" />
      <div className="mx-auto w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center">
          {organizationLogo ? (
            <img src={organizationLogo} alt={`${organizationName} logo`} className="max-h-20 w-auto rounded-xl border bg-card p-2" />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Briefcase className="h-6 w-6" />
            </div>
          )}
          <h1 className="mt-4 text-2xl font-bold text-foreground">Set new password</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {organizationQuery.isLoading
              ? 'Resolving organization details...'
              : `Your new password for ${organizationName} must be different from previously used passwords`}
          </p>
        </div>

        <Card className="border-border/70 bg-card/95 shadow-xl shadow-slate-900/5 backdrop-blur">
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl">Reset password</CardTitle>
            <CardDescription>Enter your new password below</CardDescription>
          </CardHeader>
          <CardContent>
            <BackendServerSelector disabled={isLoading} className="mb-4" />
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new_password">New password</Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                    disabled={isLoading}
                    {...register('new_password')}
                    aria-invalid={!!errors.new_password}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {errors.new_password && (
                  <p className="text-sm text-destructive">{errors.new_password.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm password</Label>
                <div className="relative">
                  <Input
                    id="confirm_password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                    disabled={isLoading}
                    {...register('confirm_password')}
                    aria-invalid={!!errors.confirm_password}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {errors.confirm_password && (
                  <p className="text-sm text-destructive">{errors.confirm_password.message}</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading && <Spinner className="mr-2" />}
                Reset password
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to={loginHref}
                className="inline-flex items-center text-sm font-medium text-primary hover:underline"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
